import { Navigate, NavLink, useParams } from "react-router-dom";
import { useEffect, useState, type ChangeEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { CatalogSettings } from "@/components/settings/catalog-settings";
import { CompanySettings } from "@/components/settings/company-settings";
import { FilesSettings } from "@/components/settings/files-settings";
import { UsersSettings } from "@/components/settings/users-settings";
import { useAuth } from "@/components/auth/auth-provider";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiGet, apiPatch } from "@/lib/client/api";
import { primarySettingsSections } from "@/lib/settings/navigation";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";
import { cn } from "@/lib/utils/cn";

export function SettingsPage() {
  const { user } = useAuth();
  const { section } = useParams();
  const { adminLogoFileId, faviconFileId, headerSubtitle, headerTitle, setServerSiteSettings, setSiteTitle, siteTitle, t, language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const activeSection = section ?? "";
  const [siteForm, setSiteForm] = useState({
    siteTitle,
    headerTitle,
    headerSubtitle,
    faviconFileId: faviconFileId ?? "",
    adminLogoFileId: adminLogoFileId ?? "",
  });
  const [savingSite, setSavingSite] = useState(false);
  const [siteMessage, setSiteMessage] = useState<string | null>(null);
  const [siteError, setSiteError] = useState<string | null>(null);

  useEffect(() => {
    async function loadSiteSettings() {
      try {
        const settings = await apiGet<typeof siteForm>("/api/site-settings");
        setSiteForm({
          siteTitle: settings.siteTitle,
          headerTitle: settings.headerTitle,
          headerSubtitle: settings.headerSubtitle,
          faviconFileId: settings.faviconFileId ?? "",
          adminLogoFileId: settings.adminLogoFileId ?? "",
        });
        setServerSiteSettings(settings);
      } catch {
        // Keep the already loaded provider settings.
      }
    }

    void loadSiteSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveSiteSettings() {
    setSavingSite(true);
    setSiteError(null);
    setSiteMessage(null);

    try {
      const settings = await apiPatch<
        {
          siteTitle: string;
          headerTitle: string;
          headerSubtitle: string;
          faviconFileId: string | null;
          adminLogoFileId: string | null;
        },
        typeof siteForm
      >("/api/site-settings", {
        siteTitle: siteForm.siteTitle,
        headerTitle: siteForm.headerTitle,
        headerSubtitle: siteForm.headerSubtitle,
        faviconFileId: siteForm.faviconFileId || null,
        adminLogoFileId: siteForm.adminLogoFileId || null,
      });
      setServerSiteSettings(settings);
      setSiteTitle(settings.siteTitle);
      setSiteMessage(text.siteSaved);
    } catch (saveError) {
      setSiteError(saveError instanceof Error ? saveError.message : text.siteSaveError);
    } finally {
      setSavingSite(false);
    }
  }

  async function uploadSiteAsset(event: ChangeEvent<HTMLInputElement>, target: "faviconFileId" | "adminLogoFileId") {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setSiteError(null);
    setSiteMessage(null);

    try {
      const formData = new FormData();
      formData.set("file", file);
      formData.set("purpose", "attachment");
      const response = await fetch("/api/files/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const payload = (await response.json()) as { id?: string; error?: string };

      if (!response.ok || !payload.id) {
        throw new Error(payload.error ?? text.uploadError);
      }

      setSiteForm((current) => ({ ...current, [target]: payload.id ?? "" }));
      setSiteMessage(text.uploaded);
    } catch (uploadError) {
      setSiteError(uploadError instanceof Error ? uploadError.message : text.uploadError);
    } finally {
      event.target.value = "";
    }
  }

  if (activeSection === "fonts" || activeSection === "terms") {
    return <Navigate to={`${workbenchRoutes.settingsSection("templates")}?panel=${activeSection}`} replace />;
  }

  if (user?.role !== "admin") {
    return <Navigate to={workbenchRoutes.dashboard} replace />;
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {primarySettingsSections.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.id === "general"}
            className={({ isActive }) =>
              cn(
                "inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-medium transition hover:bg-muted",
                isActive && "border-primary bg-primary text-primary-foreground hover:bg-primary/90",
              )
            }
          >
            {t(item.titleKey)}
          </NavLink>
        ))}
      </div>

      {activeSection === "users" ? <UsersSettings /> : null}
      {activeSection === "company" ? <CompanySettings /> : null}
      {activeSection === "templates" ? <CatalogSettings /> : null}
      {activeSection === "files" ? <FilesSettings /> : null}
      {activeSection && !["users", "company", "templates", "files"].includes(activeSection) ? (
        <Card className="p-4 text-sm text-muted-foreground">{t("settings.description")}</Card>
      ) : null}
      {activeSection ? null : (
        <>
          <div>
            <h1 className="text-2xl font-semibold">{t("settings.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("settings.description")}</p>
          </div>
          <Card className="p-4">
            <h2 className="font-medium">{t("settings.general")}</h2>
            {siteMessage || siteError ? (
              <div className={siteError ? "mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "mt-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
                {siteError ?? siteMessage}
              </div>
            ) : null}
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-medium">{t("settings.siteTitle")}</span>
                <Input
                  className="mt-2"
                  value={siteForm.siteTitle}
                  placeholder={t("settings.siteTitlePlaceholder")}
                  onChange={(event) => setSiteForm({ ...siteForm, siteTitle: event.target.value })}
                />
                <span className="mt-2 block text-xs text-muted-foreground">{t("settings.siteTitleHelp")}</span>
              </label>
              <label className="block">
                <span className="text-sm font-medium">{text.headerTitle}</span>
                <Input className="mt-2" value={siteForm.headerTitle} onChange={(event) => setSiteForm({ ...siteForm, headerTitle: event.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm font-medium">{text.headerSubtitle}</span>
                <Input className="mt-2" value={siteForm.headerSubtitle} onChange={(event) => setSiteForm({ ...siteForm, headerSubtitle: event.target.value })} />
              </label>
              <AssetField label={text.adminLogo} uploadLabel={text.upload} value={siteForm.adminLogoFileId} onChange={(value) => setSiteForm({ ...siteForm, adminLogoFileId: value })} onUpload={(event) => void uploadSiteAsset(event, "adminLogoFileId")} />
              <AssetField label={text.favicon} uploadLabel={text.upload} value={siteForm.faviconFileId} onChange={(value) => setSiteForm({ ...siteForm, faviconFileId: value })} onUpload={(event) => void uploadSiteAsset(event, "faviconFileId")} />
            </div>
            <div className="mt-4 flex justify-end">
              <Button type="button" disabled={savingSite} onClick={() => void saveSiteSettings()}>
                {savingSite ? text.saving : text.save}
              </Button>
            </div>
          </Card>
        </>
      )}
    </section>
  );
}

function AssetField({
  label,
  onChange,
  onUpload,
  uploadLabel,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  uploadLabel: string;
  value: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto]">
        <Input value={value} onChange={(event) => onChange(event.target.value)} />
        <span className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-border px-3 text-sm font-medium hover:bg-muted">
          {uploadLabel}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </span>
      </div>
    </label>
  );
}

const en = {
  headerTitle: "Admin header text",
  headerSubtitle: "Admin header subtitle",
  adminLogo: "Admin logo file",
  favicon: "Website favicon",
  save: "Save settings",
  saving: "Saving...",
  upload: "Upload",
  uploaded: "Asset uploaded.",
  uploadError: "Unable to upload asset.",
  siteSaved: "Site settings saved.",
  siteSaveError: "Unable to save site settings.",
};

const zh: typeof en = {
  headerTitle: "后台左上角文字",
  headerSubtitle: "后台左上角副标题",
  adminLogo: "后台 Logo 文件",
  favicon: "网站 Favicon",
  save: "保存设置",
  saving: "保存中...",
  upload: "上传",
  uploaded: "文件已上传。",
  uploadError: "无法上传文件。",
  siteSaved: "网站设置已保存。",
  siteSaveError: "无法保存网站设置。",
};
